// Reward-chrome control (demo view only) — story-flow P1 #8. Switches the
// INTERIOR camera between the two curated rail modes:
//   · orbit — sweeps around the room (the loop's default interior view)
//   · walk  — travels through the room
// Both are framed rails, so neither exposes a bad photogrammetry angle (which is
// why the interior is on rails and freeform stays exterior-only). Hidden through
// the guided first-run; the story-director reveals it on RETURN→SANDBOX as part
// of the reward chrome, alongside the era toggle + scrubbable timeline.
//
// Orbit/walk only mean something inside, so the control dims + goes inert when
// the visitor is in the exterior (freeform) view.

const ACTIVE = [125, 184, 232] as const; // cyan — matches the demo chrome accent

export interface CameraModeToggleDeps {
  /** Outside vs inside — the control is only live inside. */
  getView: () => 'exterior' | 'interior';
  /** The live interior rail mode, or null when outside / not on a rail cam. */
  mode: () => 'orbit' | 'walk' | null;
  /** Switch the interior rail camera (a no-op when outside). */
  setMode: (m: 'orbit' | 'walk') => void;
}

export interface CameraModeToggle {
  /** Per-frame (demo render loop): reflect view + mode changes (write-on-change). */
  update(): void;
  /** Fade in + enable — the reward reveal (RETURN→SANDBOX). */
  reveal(): void;
  dispose(): void;
}

export function createCameraModeToggle(deps: CameraModeToggleDeps): CameraModeToggle {
  const wrap = document.createElement('div');
  wrap.id = 'lec-camera-mode';
  wrap.style.cssText = [
    'position: fixed',
    'bottom: 22px',
    'right: 22px',
    'display: flex',
    'align-items: center',
    'gap: 6px',
    'padding: 6px 11px',
    'background: rgba(20, 26, 38, 0.58)',
    'border: 1px solid rgba(150, 178, 228, 0.40)',
    'border-radius: 999px',
    'backdrop-filter: blur(7px)',
    '-webkit-backdrop-filter: blur(7px)',
    // Legibility (John, 2026-06-29: larger + brighter): up from 10px/0.7-alpha.
    'font: 600 12px/1 system-ui, sans-serif',
    'color: rgba(238, 244, 252, 0.96)',
    'user-select: none',
    'pointer-events: none',
    'z-index: 1000',
    'opacity: 0',
    'transition: opacity 700ms ease',
  ].join('; ');

  const cap = document.createElement('span');
  cap.textContent = 'camera';
  cap.style.cssText = 'letter-spacing: 0.06em; opacity: 0.7;';
  wrap.appendChild(cap);

  const mk = (m: 'orbit' | 'walk'): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = m;
    b.title = m === 'orbit' ? 'orbit the room (sweeping rail)' : 'walk through the room (travelling rail)';
    b.style.cssText = [
      'text-transform: uppercase',
      'letter-spacing: 0.08em',
      'font: inherit',
      'font-size: 9px',
      'padding: 3px 8px',
      'border-radius: 999px',
      'cursor: pointer',
      'background: rgba(255,255,255,0.05)',
      'border: 1px solid rgba(255,255,255,0.14)',
      'color: inherit',
      'transition: background 140ms ease, color 140ms ease, border-color 140ms ease',
    ].join('; ');
    b.addEventListener('click', () => deps.setMode(m));
    return b;
  };
  const orbitBtn = mk('orbit');
  const walkBtn = mk('walk');
  wrap.append(orbitBtn, walkBtn);
  document.body.appendChild(wrap);

  let revealed = false;
  const reveal = (): void => { revealed = true; update(); };

  // Write-on-change (PF6): a still scene touches no DOM.
  let lastMode: 'orbit' | 'walk' | null | undefined;
  let lastInside: boolean | undefined;
  const paint = (active: 'orbit' | 'walk' | null, inside: boolean): void => {
    const [r, g, b] = ACTIVE;
    for (const [m, el] of [['orbit', orbitBtn], ['walk', walkBtn]] as const) {
      const on = inside && active === m;
      el.style.background = on ? `rgba(${r}, ${g}, ${b}, 0.22)` : 'rgba(255,255,255,0.05)';
      el.style.borderColor = on ? `rgb(${r}, ${g}, ${b})` : 'rgba(255,255,255,0.14)';
      el.style.color = on ? `rgb(${r}, ${g}, ${b})` : 'rgba(234,241,255,0.7)';
    }
    // Outside, orbit/walk are meaningless — dim the control + make it inert.
    wrap.style.opacity = inside ? '1' : '0.4';
    wrap.style.pointerEvents = inside ? 'auto' : 'none';
    wrap.title = inside ? '' : 'step inside to switch the interior camera';
  };

  const update = (): void => {
    if (!revealed) return;
    const inside = deps.getView() === 'interior';
    const m = deps.mode();
    if (m === lastMode && inside === lastInside) return;
    lastMode = m; lastInside = inside;
    paint(m, inside);
  };

  return {
    update,
    reveal,
    dispose() { wrap.remove(); },
  };
}
